import { formatMoney, type HistoryEntryDto } from '@nsb/core';
import { dateWords } from '../../days.ts';

/**
 * A step's log as its panel reads it (design study 31, slice 3): the latest quote at the top of the step, each task's
 * count on its row, and a task's own entries when its count is pressed. The log comes newest first from the API.
 */

/** Where a step's log is kept on the page: under its project, so a change on the project reads it again. */
export const stepLogKey = (projectId: string, stepId: string) => ['project', projectId, 'step-log', stepId];

/** The latest quote on a step's log, its tasks' with it: by its day, then its time; of two alike, the newer made. */
export function latestQuote(entries: readonly HistoryEntryDto[]): HistoryEntryDto | null {
  let best: HistoryEntryDto | null = null;
  for (const e of entries) {
    if (e.kind !== 'quote') continue;
    const later = !best || e.happenedOn > best.happenedOn || (e.happenedOn === best.happenedOn && (e.happenedAt ?? '') > (best.happenedAt ?? ''));
    if (later) best = e;
  }
  return best;
}

/** A quote in a line: its amount, who it is from and its day ("Quote: $18,500 from DC Structural, Oct 27"); a blank amount is no figure. */
export function quoteWords(e: HistoryEntryDto): string {
  const amount = e.amountCents !== null ? `: ${formatMoney(e.amountCents)}` : '';
  const from = e.whoFrom ? ` from ${e.whoFrom}` : '';
  return `Quote${amount}${from}, ${dateWords(e.happenedOn)}`;
}

/** How many entries each step of the log is on: a task's count, shown on its row. */
export function logCounts(entries: readonly HistoryEntryDto[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const e of entries) if (e.step) out.set(e.step.id, (out.get(e.step.id) ?? 0) + 1);
  return out;
}

/** A task's own entries, or the whole step's when none is asked for. */
export const forTask = (entries: readonly HistoryEntryDto[], taskId: string | null): HistoryEntryDto[] =>
  taskId === null ? [...entries] : entries.filter((e) => e.step?.id === taskId);
